/**
 * Centralized dev user ID management for testing and development
 * 
 * This module provides a single source of truth for dev user IDs,
 * ensuring consistency across the codebase.
 */

import { DEV_USER_ID as ENV_DEV_USER_ID } from '@/config';

/**
 * Default dev user ID constant - single source of truth
 * This is the standard mock user ID used throughout the application
 * Falls back to this constant if DEV_USER_ID env var is not set
 */

/**
 * Exported dev user ID - uses env var if set, otherwise falls back to constant
 */
export const DEV_USER_ID = ENV_DEV_USER_ID ;

// Debug logging (only in development, server-side only)
if (typeof window === 'undefined' && process.env.NODE_ENV === 'development') {
  console.log('[dev-user] Server-side resolution:');
  console.log('  - ENV_DEV_USER_ID:', ENV_DEV_USER_ID);
  console.log('  - Final DEV_USER_ID:', DEV_USER_ID);
  console.log('  - process.env.DEV_USER_ID:', process.env.DEV_USER_ID);
}

/**
 * Checks if a given user ID is the dev/mock user ID
 * Compares against both the env var value and the constant
 * 
 * @param userId - The user ID to check
 * @returns true if the userId matches the dev user ID
 */
export function isDevUser(userId: string | null | undefined): boolean {
  if (!userId) return false;
  const currentDevUserId = getDevUserId() || '';
  return userId === currentDevUserId ;
}

/**
 * Gets the dev user ID to use, respecting priority:
 * 1. DEV_USER_ID env var (if set)
 * 2. DEV_USER_ID constant (fallback)
 * 
 * @returns The dev user ID to use
 */
export function getDevUserId(): string {
  return DEV_USER_ID || '';
}

/**
 * Resolves dev user ID from request headers, query params, or defaults
 * Used in API routes to allow flexible dev user ID resolution
 * 
 * @param request - Optional Request object to extract headers/query params
 * @returns The resolved dev user ID, or null if not found
 */
export function resolveDevUserId(request?: Request): string | null {
  if (!request) {
    return getDevUserId();
  }

  // Check headers first (x-user-id or x-user_id)
  const headerUserId = request.headers.get('x-user-id') || request.headers.get('x-user_id');
  if (headerUserId) {
    return headerUserId;
  }

  // Check query params
  try {
    const url = new URL(request.url);
    const qpUser = url.searchParams.get('user_id');
    if (qpUser) {
      return qpUser;
    }
  } catch (e) {
    // Invalid URL, continue to fallback
  }

  // Fallback to default
  return getDevUserId();
}

